import _ from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';

import MapBox from './filters/map-box/MapBox.react';
import FiltersComponent from './FiltersComponent.react';
import TravelPolicyComponent from './TravelPolicyComponent.react';
import TravelPolicyLoader from './TravelPolicyLoader.react';
import PrivateTravelComponent from './PrivateTravelComponent.react';
import SortDropdownComponent from './SortDropdownComponent.react';
import PointsToggleComponent from './PointsToggleComponent.react';
import SequenceProgress from './SequenceProgress.react';
import ResultsComponent from './ResultsComponent.react';
import ResultsStore from '../../../stores/ResultsStore';
import SearchStore from '../../../stores/SearchStore';
import SearchFull from '../../search/SearchFull.react';
import MapStore from '../../../stores/MapStore';
import ProfileStore from '../../../stores/ProfileStore';
import PointsStore from '../../../stores/PointsStore';
import { VARIANT } from '../../search/constants';
import { MAIN_BUSINESS_LINE } from '@arbitrip/constants';
import ScrollToTop from 'react-scroll-up';

import Analytics from 'arbitrip-common/client/analytics';
import { getPointsToggleState } from 'arbitrip-common/client/analytics/services/loyaltyService';

import ExpiredResultsComponent from '../../general/expired-results/ExpiredResultsComponent.react';
import SortActions from '../../../actions/SortActions';
import PointsActions from '../../../actions/PointsActions';
import SortConstants from '../../../constants/SortConstants';
import { isElalCompany } from '@/utils/isCompany';
import { withSearchParams } from '../../../utils/withRouter';
import { filtersToParams, paramsToFilters, FILTER_PARAM_KEYS } from '../../../utils/filterQueryParams';
import { sortToParam, paramToSort, SORT_PARAM_KEY } from '../../../utils/sortQueryParams';

import SearchProgress from './SearchProgress.react';
import * as Sentry from '@sentry/react';
import useSamlPageVerification from '@/hooks/useSamlPageVerification';
import { ANONYMOUS_PERMISSION_SEARCH_RESULTS } from 'arbitrip-common/client/utils/ElalSamlVerification';

const PARTNER_WIDGET_HANDOFF_PARAMS = ['saml_verified', 'search_token', 'place_id', 'hotel_id', 'check_in'];

function getResultsPageState() {
	return {
		initialFullMapMode: MapStore.getFullMapMode(),
		profile: ProfileStore.getProfile(),
		search_terms: _.get(ResultsStore.getCurrentSearchSession(), 'search_terms'),
		breakfast_only: ResultsStore.isBreakfastOnly(),
		search_terms_valid: SearchStore.isValid(),
		private_travel: SearchStore.isPrivateTravel(),
		search_results: ResultsStore.getSearchResults(),
		search_session_created_timestamp: ResultsStore.getSearchSessionCreatedTimestamp(),
		travel_policy: ResultsStore.getTravelPolicy(),
		filters: ResultsStore.getFilters(),
		sort_method: ResultsStore.getSortMethod(),
		regions: ResultsStore.getRegions(),
		is_show_hotel_loader: SearchStore.isShowHotelLoader(),
	};
}

function SamlVerificationRunner() {
	useSamlPageVerification(ANONYMOUS_PERMISSION_SEARCH_RESULTS);
	return null;
}

class ResultsPage extends React.Component {
	constructor(props) {
		super(props);

		this.state = {
			...getResultsPageState(),
			mapBoxOpen: false,
			isLoadingDeals: true
		};

		this.handleMapBoxState = this.handleMapBoxState.bind(this);
		this._onChange = this._onChange.bind(this);
		this._onProfileChange = this._onProfileChange.bind(this);
		this._onSearchChange = this._onSearchChange.bind(this);
		this.onDealsLoaded = this.onDealsLoaded.bind(this);
		this.syncToUrl = this.syncToUrl.bind(this);
	}

	syncToUrl() {
		const filterParams = filtersToParams(this.state.filters);
		const sortParam = sortToParam(this.state.sort_method);
		this.props.setSearchParams(prev => {
			const next = new URLSearchParams(prev);
			FILTER_PARAM_KEYS.forEach(k => next.delete(k));
			Object.entries(filterParams).forEach(([k, v]) => next.set(k, v));
			next.delete(SORT_PARAM_KEY);
			Object.entries(sortParam).forEach(([k, v]) => next.set(k, v));
			return next;
		}, { replace: true });
	}

	componentDidUpdate(_prevProps, prevState) {
		if (prevState.search_session_created_timestamp !== this.state.search_session_created_timestamp) {
			this.setState({ isLoadingDeals: true });
		}
	}

	componentDidMount() {
		const { profile } = this.state;

		ProfileStore.addChangeListener(this._onProfileChange);
		ResultsStore.addChangeListener(this._onChange);
		SearchStore.addChangeListener(this._onSearchChange);

		this._maybeFetchElalAnonPointsRate(profile);

		const filtersFromUrl = paramsToFilters(this.props.searchParams);
		if (Object.keys(filtersFromUrl).length > 0) {
			ResultsStore.applyFilters(filtersFromUrl);
		}

		const sortFromUrl = paramToSort(this.props.searchParams);
		if (sortFromUrl) {
			const SORT_ACTION_MAP = {
				[SortConstants.SORT_METHOD.RECOMMENDED]: SortActions.sortByRecommended,
				[SortConstants.SORT_METHOD.SCORES]: SortActions.sortByScores,
				[SortConstants.SORT_METHOD.PRICE]: SortActions.sortByPrice,
				[SortConstants.SORT_METHOD.PRICE_HIGH_TO_LOW]: SortActions.sortByPriceHighToLow,
				[SortConstants.SORT_METHOD.DISTANCE]: SortActions.sortByDistance,
				[SortConstants.SORT_METHOD.REVIEWS]: SortActions.sortByReviews,
				[SortConstants.SORT_METHOD.ABSOLUTE_MARGIN]: SortActions.sortByAbsoluteMargin,
				[SortConstants.SORT_METHOD.MARGIN_PERCENTAGE]: SortActions.sortByMarginPercentage,
			};
			const action = SORT_ACTION_MAP[sortFromUrl];
			if (action) action();
		}

		const params = new URLSearchParams(window.location.search);
		const cameFromPartnerWidget = PARTNER_WIDGET_HANDOFF_PARAMS.some((param) => params.has(param));
		const searchTerms = SearchStore.getSearchTerms();
		Analytics.actions.views.resultsPage(profile, null, {
			cameFromPartnerWidget,
			is_loyalty_member: !!(profile && profile.loyalty && profile.loyalty.enabled),
			points_balance: PointsStore.getBalance(),
			points_toggle_state: getPointsToggleState(ResultsStore.getShowPricesInPoints()),
			search_destination: searchTerms?.destination?.address,
			search: searchTerms,
			results_count: ResultsStore.getRecommendedCount(),
		});
	}

	componentWillUnmount() {
		SearchStore.removeChangeListener(this._onSearchChange);
		ProfileStore.removeChangeListener(this._onProfileChange);
		ResultsStore.removeChangeListener(this._onChange);
		// AppGridStore.removeChangeListener(this._onChange);
	}

	handleMapBoxState(bool) {
		this.setState({ mapBoxOpen: bool });
	}
	onDealsLoaded(){
		this.setState({isLoadingDeals:false})
	}
	render() {
		const {
			mapBoxOpen,
			profile,
			search_session_created_timestamp,
			travel_policy,
			filters,
			regions,
			search_terms,
			is_show_hotel_loader,
		} = this.state;

		const { t } = this.props;

		const scrollToTopStyle = {
			position: 'fixed',
			bottom: 0, //50,
			right: 'auto', //30,
			cursor: 'pointer',
			transitionDuration: '0.2s',
			transitionTimingFunction: 'linear',
			transitionDelay: '0s',
		};

		const searchResultsStyle = this.state.initialFullMapMode ? { paddingTop: '512px' } : {}; // paddingTop: '0' };

		const TRAVEL_POLICY =
			travel_policy || profile.agent ? (
				<TravelPolicyComponent profile={profile} travelPolicy={travel_policy} />
			) : (
				<TravelPolicyLoader />
			);
			
		const isB2C = !profile?.agent && profile?.company?.main_business_line === MAIN_BUSINESS_LINE.B2C;

		return (
			<React.Fragment>
				<SamlVerificationRunner />
				<div className="results-progress">
					<SearchProgress
						searchSessionCreatedTimestamp={this.state.search_session_created_timestamp}
						results={this.state.search_results}
					/>
				</div>
				<div className="results-page">
					<div className="filters-container">
						<div className="filters">
							<MapBox
								handleMapBoxState={this.handleMapBoxState}
								profile={profile}
								searchTerms={search_terms}
								searchResults={this.state.search_results}
								breakfastOnly={this.state.breakfast_only}
								filters={filters}
								regions={regions}
							/>
						</div>
						{!mapBoxOpen ? (
							<React.Fragment>
								<FiltersComponent
									profile={profile}
									travelPolicy={travel_policy}
									filters={filters}
									search_terms={search_terms}
									showDistanceFilter={true}
								/>
								<ScrollToTop
									showUnder={716}
									topPosition={0}
									easing="easeOutCubic"
									duration={250}
									style={scrollToTopStyle}
								>
									<div className="scroll-to-top">
										<i
											className="fa fa-chevron-circle-up"
											aria-hidden="true"
											title={t('GoToTop')}
										/>
									</div>
								</ScrollToTop>
							</React.Fragment>
						) : (
							<div style={{ minHeight: 1000 }} />
						)}
					</div>
					<div className="search-results-container" id="search-results" style={searchResultsStyle}>
						<SearchFull variant={VARIANT.SMALL} />
						{( isB2C && this.state.isLoadingDeals ) && <SequenceProgress
						 	searchSessionCreatedTimestamp={this.state.search_session_created_timestamp}
							results={this.state.search_results}
							onDealsLoaded={this.onDealsLoaded}
							t={t}
						/>}
						{( !isB2C || ( isB2C && !this.state.isLoadingDeals)) && <div className="results-actions">
							{ProfileStore.isLeisureExperienceEnabled() ? null : this.state.private_travel ? (
								<PrivateTravelComponent />
							) : profile.travel_policy_enabled || profile.agent ? (
								TRAVEL_POLICY
							) : null}
							<PointsToggleComponent withTooltip/>
							<SortDropdownComponent />
						</div>}

						<ResultsComponent
							profile={profile}
							searchSessionCreatedTimestamp={search_session_created_timestamp}
							searchTerms={search_terms}
							showHotelLoading={is_show_hotel_loader}
						/>
					</div>

					<ExpiredResultsComponent expiredEntityText={t('Results')} />
				</div>
			</React.Fragment>
		);
	}

	_onChange() {
		this.setState(getResultsPageState(), this.syncToUrl);
	}

	_maybeFetchElalAnonPointsRate(profile) {
		if (isElalCompany() && profile && profile.anonymous && !profile.isLoggedIn && !this._elalAnonRateFetched) {
			this._elalAnonRateFetched = true;
			PointsActions.getElalAnonPointsRate();
		}
	}

	_onProfileChange() {
		const profile = ProfileStore.getProfile();
		this.setState({ profile });
		this._maybeFetchElalAnonPointsRate(profile);
	}

	_onSearchChange() {
		this.setState({
			search_terms: _.get(ResultsStore.getCurrentSearchSession(), 'search_terms'),
			search_terms_valid: SearchStore.isValid(),
			is_show_hotel_loader: SearchStore.isShowHotelLoader(),
		});
	}
}

Sentry.withErrorBoundary(ResultsComponent);

ResultsPage.propTypes = {
	route: PropTypes.object,
};

export default withTranslation()(withSearchParams(ResultsPage));
